import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { getUTMs } from "../../hooks/useUTMs";
import ConsentCheckboxes, { honeypotValue } from "./ConsentCheckboxes";
import {
  Tablet,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Sparkles,
  ShieldCheck,
  User,
  Phone,
  Mail,
  RotateCcw,
  Home,
  Download,
  BookOpen
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const EBOOK_PDF_URL = "/downloads/O_Codigo_da_Riqueza.pdf";

const questions = [
  {
    id: "q1",
    stepTitle: "Etapa 1 de 3 · Relação Financeira",
    question: "Como você resume a sua relação com o dinheiro hoje?",
    options: [
      {
        key: "A",
        label: "Meu dinheiro trabalha para construir meu patrimônio.",
        sub: "Foco em multiplicação, investimentos e visão de longo prazo."
      },
      {
        key: "B",
        label: "Meu dinheiro só serve para pagar os boletos do mês.",
        sub: "Foco na rotina, contas recorrentes e dificuldade em guardar/acumular."
      }
    ]
  },
  {
    id: "q2",
    stepTitle: "Etapa 2 de 3 · Diagnóstico e Desafios",
    subtitle: "Identificação da principal barreira",
    question: "Na sua opinião, o que mais impede o seu crescimento financeiro hoje?",
    options: [
      {
        key: "A",
        label: "A falta de planejamento e clareza.",
        sub: "Não ter uma rota clara ou estratégia definida para alavancar."
      },
      {
        key: "B",
        label: "Os juros abusivos na hora de buscar crédito.",
        sub: "Custo alto dos financiamentos e crédito bancário tradicional."
      },
      {
        key: "C",
        label: "O medo de cair em promessas financeiras milagrosas.",
        sub: "Insegurança com riscos desnecessários e consultorias duvidosas."
      }
    ]
  }
];

export default function ExpointerSurvey() {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({
    q1: "",
    q2: ""
  });
  const [leadData, setLeadData] = useState({
    name: "",
    phone: "",
    email: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketNumber, setTicketNumber] = useState("");
  const [lgpdAccepted, setLgpdAccepted] = useState(false);
  const [marketingAccepted, setMarketingAccepted] = useState(false);

  const triggerDownload = () => {
    const link = document.createElement("a");
    link.href = EBOOK_PDF_URL;
    link.download = "O_Codigo_da_Riqueza_com_Consorcio.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Download do E-book iniciado com sucesso!");
  };

  const handleSelectOption = (questionKey, optionKey, optionLabel) => {
    setAnswers((prev) => ({
      ...prev,
      [questionKey]: `${optionKey}) ${optionLabel}`
    }));
    // Avança automaticamente após uma breve animação
    setTimeout(() => {
      setStep((prev) => prev + 1);
    }, 280);
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    if (numbers.length <= 11) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setLeadData((prev) => ({ ...prev, phone: formatted }));
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();

    if (!leadData.name.trim()) {
      toast.error("Por favor, preencha seu nome completo.");
      return;
    }

    const rawPhoneDigits = leadData.phone.replace(/\D/g, "");
    if (rawPhoneDigits.length < 10) {
      toast.error("Por favor, insira um WhatsApp válido com DDD (ex: 51 99999-9999).");
      return;
    }

    if (!lgpdAccepted) {
      toast.error("Marque a autorização de contato para validar sua participação.");
      return;
    }

    setLoading(true);

    const surveyMessage = [
      "📋 PESQUISA EXPOINTER - SORTEIO TABLET",
      "----------------------------------------",
      `1. Relação com o dinheiro: ${answers.q1 || "Não respondida"}`,
      `2. O que mais impede o crescimento: ${answers.q2 || "Não respondida"}`,
      "----------------------------------------",
      "LGPD: Consentimento de uso de dados autorizado pelo participante.",
      "Origem: Estande Expointer · Cadastro para Sorteio do Tablet"
    ].join("\n");

    // E-mail real captado ou fallback para o e-mail padrão da feira
    const rawEmail = leadData.email.trim();
    if (rawEmail && !rawEmail.includes("@")) {
      toast.error("Por favor, insira um e-mail válido (ex: seu@email.com).");
      return;
    }
    const finalEmail = rawEmail || "lead@epointer.com.br";

    const payload = {
      name: leadData.name.trim(),
      phone: leadData.phone.trim(),
      email: finalEmail,
      investment_range: "100 a 200 mil",
      message: surveyMessage,
      source: "expointer",
      lgpd_accepted: true,
      marketing_consent: marketingAccepted,
      utm: getUTMs(),
      website: honeypotValue(e)
    };

    try {
      await axios.post(`${API}/leads`, payload);
      // Gera número de cupom visual para o participante
      const generatedTicket = `EXP-${Math.floor(100000 + Math.random() * 900000)}`;
      setTicketNumber(generatedTicket);
      setSubmitted(true);
      toast.success("Participação confirmada com sucesso!");

      if (window.gtag) {
        window.gtag("event", "Lead", {
          event_category: "expointer_survey",
          event_label: "sorteio_tablet"
        });
      }
      if (window.fbq) {
        window.fbq("track", "Lead", { value: 0, currency: "BRL" });
      }
    } catch (err) {
      console.error(err);
      if (err.response?.status === 409) {
        toast.error("⚠️ Este número de WhatsApp já está cadastrado. Apenas uma participação por número é permitida.", { duration: 6000 });
        setLoading(false);
        return;
      }
      let msg = "Erro ao registrar participação. Tente novamente em instantes.";
      if (err.response?.status === 502) {
        msg = "Servidor em reinicialização (502 Gateway). Tente novamente em alguns segundos.";
      } else if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === "string") msg = detail;
        else if (Array.isArray(detail) && detail[0]?.msg) msg = detail[0].msg;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResetForNextUser = () => {
    setAnswers({ q1: "", q2: "" });
    setLeadData({ name: "", phone: "", email: "" });
    setStep(1);
    setSubmitted(false);
    setTicketNumber("");
    toast.info("Pronto para o próximo participante!");
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col justify-between selection:bg-primary/20 selection:text-primary">
      {/* Top Header */}
      <header className="border-b border-border/60 bg-background/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container max-w-4xl h-16 flex items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2 group">
              <span className="text-gold-gradient font-display text-xl font-bold tracking-tight">
                Juliano Wagner
              </span>
              <span className="hidden sm:inline text-[10px] uppercase tracking-[0.25em] text-muted-foreground border-l border-border/80 pl-2">
                Consultoria Patrimonial
              </span>
            </Link>
          </div>

          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-1.5 bg-primary/10 border border-primary/30 px-3 py-1 rounded-full text-xs font-medium text-primary">
              <Sparkles size={13} className="text-primary animate-pulse" />
              <span>Expointer 2026</span>
            </div>

            <Link
              to="/"
              className="p-2 text-muted-foreground hover:text-foreground transition-colors rounded-full hover:bg-white/5"
              title="Voltar para a página principal"
            >
              <Home size={18} />
            </Link>
          </div>
        </div>

        {/* Progress Bar */}
        {!submitted && (
          <div className="w-full bg-border/40 h-1">
            <div
              className="h-1 transition-all duration-500 ease-out"
              style={{
                width: `${(step / 3) * 100}%`,
                background: "var(--gold-gradient)"
              }}
            />
          </div>
        )}
      </header>

      {/* Main Content Area */}
      <main className="flex-1 flex items-center justify-center p-4 sm:p-6 md:p-8">
        <div className="w-full max-w-2xl mx-auto">
          <AnimatePresence mode="wait">
            {submitted ? (
              /* Success / Thank You Screen */
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-card-gradient border border-primary/40 shadow-gold rounded-2xl p-8 sm:p-12 text-center relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-48 h-48 bg-primary/10 rounded-full blur-3xl -z-10" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary/5 rounded-full blur-3xl -z-10" />

                <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center bg-primary/10 ring-2 ring-primary/40 text-primary">
                  <Tablet size={38} className="animate-bounce" />
                </div>

                <span className="inline-block text-xs uppercase tracking-[0.25em] text-primary font-mono mb-2">
                  Participação Confirmada
                </span>
                <h2 className="font-display text-3xl sm:text-4xl font-light mb-3">
                  Você está concorrendo ao <span className="italic text-gold-gradient font-normal">Tablet</span>!
                </h2>

                <p className="text-muted-foreground text-sm sm:text-base max-w-md mx-auto mb-6 leading-relaxed">
                  Obrigado, <strong className="text-foreground">{leadData.name}</strong>. Seus dados foram validados e nosso time entrará em contato via WhatsApp para apresentar o seu plano financeiro passo a passo.
                </p>

                {ticketNumber && (
                  <div className="bg-background/80 border border-border/80 rounded-xl p-4 max-w-xs mx-auto mb-6 shadow-inner">
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">
                      Cupom do Sorteio
                    </span>
                    <span className="font-mono text-xl sm:text-2xl font-bold text-gold-gradient tracking-wider">
                      {ticketNumber}
                    </span>
                  </div>
                )}

                {/* Cortesia Exclusiva - Download do E-book */}
                <div className="bg-background/90 border border-primary/30 rounded-2xl p-5 sm:p-6 mb-6 shadow-gold text-left relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-2xl -z-10" />
                  
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="w-16 h-20 rounded-lg overflow-hidden border border-primary/40 shadow-sm shrink-0 bg-black/40 hidden sm:block">
                      <img
                        src="/images/ebook-cover.png"
                        alt="E-book O Código da Riqueza com Consórcio"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="inline-flex items-center gap-1.5 bg-primary/15 text-primary text-[10px] font-mono uppercase tracking-widest px-2.5 py-0.5 rounded-full mb-1.5">
                        <BookOpen size={11} /> Cortesia Exclusiva de Agradecimento
                      </div>
                      <h3 className="font-display text-lg sm:text-xl font-medium text-foreground mb-1">
                        E-book: O Código da Riqueza com Consórcio
                      </h3>
                      <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                        Acesse as estratégias comprovadas de alavancagem patrimonial do consultor Juliano Wagner para multiplicar seu patrimônio.
                      </p>
                      
                      <button
                        type="button"
                        onClick={triggerDownload}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full font-medium text-xs sm:text-sm transition-all shadow-gold hover:translate-y-[-1px] cursor-pointer"
                        style={{ background: "var(--gold-gradient)", color: "#0f0d0a" }}
                      >
                        <Download size={15} /> Baixar Meu E-book Gratuito (PDF)
                      </button>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-border/60 flex flex-col sm:flex-row gap-3 justify-center items-center">
                  <Link
                    to="/?concluiu_quiz=1"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-medium text-sm transition-all shadow-gold hover:translate-y-[-1px] cursor-pointer"
                    style={{ background: "var(--gold-gradient)", color: "#0f0d0a" }}
                  >
                    <Home size={16} /> Voltar ao Site Oficial
                  </Link>

                  <button
                    onClick={handleResetForNextUser}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-medium text-sm border border-border hover:bg-white/5 transition-colors text-muted-foreground hover:text-foreground cursor-pointer"
                  >
                    <RotateCcw size={16} /> Cadastrar Próximo Participante
                  </button>
                </div>
              </motion.div>
            ) : step === 1 ? (
              /* Step 1: Question 1 */
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-card-gradient border border-primary/20 shadow-gold rounded-2xl p-6 sm:p-10"
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="text-[11px] uppercase tracking-[0.2em] font-mono text-primary font-semibold">
                    Etapa 1 de 3 · Pergunta Rápida
                  </span>
                  <span className="text-xs text-muted-foreground font-mono">33%</span>
                </div>

                <h1 className="text-2xl sm:text-3xl md:text-4xl font-display font-light mb-8 leading-snug">
                  Como você resume a sua{" "}
                  <span className="italic text-gold-gradient font-normal">relação com o dinheiro</span> hoje?
                </h1>

                <div className="space-y-4 mb-8">
                  {questions[0].options.map((opt) => {
                    const isSelected = answers.q1.startsWith(opt.key);
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => handleSelectOption("q1", opt.key, opt.label)}
                        className={`w-full text-left p-5 rounded-xl border transition-all duration-200 flex items-start gap-4 ${
                          isSelected
                            ? "border-primary bg-primary/10 shadow-gold ring-1 ring-primary/40"
                            : "border-border bg-background/50 hover:border-primary/50 hover:bg-background/80"
                        }`}
                      >
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center font-mono font-bold text-sm shrink-0 transition-colors ${
                            isSelected
                              ? "bg-primary text-primary-foreground"
                              : "bg-white/5 text-muted-foreground border border-border"
                          }`}
                        >
                          {opt.key}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-foreground text-base sm:text-lg mb-1">
                            {opt.label}
                          </div>
                          <div className="text-xs sm:text-sm text-muted-foreground">
                            {opt.sub}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between text-xs text-muted-foreground pt-4 border-t border-border/40">
                  <span className="flex items-center gap-1.5">
                    <Tablet size={14} className="text-primary" /> Participação válida para o sorteio do Tablet
                  </span>
                  <span>Toque para avançar</span>
                </div>
              </motion.div>
            ) : step === 2 ? (
              /* Step 2: Question 2 */
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-card-gradient border border-primary/20 shadow-gold rounded-2xl p-6 sm:p-10"
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="text-[11px] uppercase tracking-[0.2em] font-mono text-primary font-semibold">
                    Etapa 2 de 3 · Identificação da Dor
                  </span>
                  <span className="text-xs text-muted-foreground font-mono">66%</span>
                </div>

                <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-light mb-8 leading-snug">
                  Na sua opinião, o que mais{" "}
                  <span className="italic text-gold-gradient font-normal">impede o seu crescimento financeiro</span> hoje?
                </h2>

                <div className="space-y-4 mb-8">
                  {questions[1].options.map((opt) => {
                    const isSelected = answers.q2.startsWith(opt.key);
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => handleSelectOption("q2", opt.key, opt.label)}
                        className={`w-full text-left p-5 rounded-xl border transition-all duration-200 flex items-start gap-4 ${
                          isSelected
                            ? "border-primary bg-primary/10 shadow-gold ring-1 ring-primary/40"
                            : "border-border bg-background/50 hover:border-primary/50 hover:bg-background/80"
                        }`}
                      >
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center font-mono font-bold text-sm shrink-0 transition-colors ${
                            isSelected
                              ? "bg-primary text-primary-foreground"
                              : "bg-white/5 text-muted-foreground border border-border"
                          }`}
                        >
                          {opt.key}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-foreground text-base sm:text-lg mb-1">
                            {opt.label}
                          </div>
                          <div className="text-xs sm:text-sm text-muted-foreground">
                            {opt.sub}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-border/40">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <ArrowLeft size={14} /> Voltar à pergunta anterior
                  </button>
                  <span className="text-xs text-muted-foreground">Toque para avançar</span>
                </div>
              </motion.div>
            ) : (
              /* Step 3: Solution Presentation & Lead Capture */
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-card-gradient border border-primary/20 shadow-gold rounded-2xl p-6 sm:p-10"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[11px] uppercase tracking-[0.2em] font-mono text-primary font-semibold">
                    Etapa 3 de 3 · Solução e Cadastro
                  </span>
                  <span className="text-xs text-muted-foreground font-mono">100%</span>
                </div>

                {/* Solution Presentation Box */}
                <div className="bg-primary/10 border border-primary/30 rounded-xl p-5 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-primary/20 text-primary shrink-0 mt-0.5">
                      <ShieldCheck size={20} />
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground text-base sm:text-lg mb-1 leading-snug">
                        Nós ajudamos a resolver essa desordem financeira.
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        Através de um plano de ação passo a passo, com disciplina e sem falsas promessas.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <h2 className="font-display text-xl sm:text-2xl font-light mb-2">
                    Para validar a sua participação no <span className="italic text-gold-gradient">sorteio do tablet</span> e receber nosso contato:
                  </h2>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Preencha seus dados abaixo para registrar seu cupom.
                  </p>
                </div>

                <form onSubmit={handleLeadSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="expointer-field-1" className="block text-sm text-muted-foreground font-medium mb-1.5">
                      Nome completo <span className="text-primary">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-muted-foreground">
                        <User size={18} />
                      </div>
                      <input
                        id="expointer-field-1"
                        type="text"
                        required
                        maxLength={100}
                        value={leadData.name}
                        onChange={(e) =>
                          setLeadData((prev) => ({ ...prev, name: e.target.value }))
                        }
                        placeholder="Ex: João da Silva"
                        className="w-full pl-10 pr-4 py-3.5 rounded-xl bg-background border border-input text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary/70 focus:ring-1 focus:ring-primary/40 font-body text-base"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="expointer-field-2" className="block text-sm text-muted-foreground font-medium mb-1.5">
                      WhatsApp <span className="text-primary">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-muted-foreground">
                        <Phone size={18} />
                      </div>
                      <input
                        id="expointer-field-2"
                        type="tel"
                        required
                        maxLength={16}
                        value={leadData.phone}
                        onChange={handlePhoneChange}
                        placeholder="(51) 99999-9999"
                        className="w-full pl-10 pr-4 py-3.5 rounded-xl bg-background border border-input text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary/70 focus:ring-1 focus:ring-primary/40 font-body text-base"
                      />
                    </div>
                    <span className="text-xs text-muted-foreground mt-1 block">
                      Entraremos em contato pelo WhatsApp para divulgar o resultado e orientações.
                    </span>
                  </div>

                  <div>
                    <label htmlFor="expointer-field-3" className="block text-sm text-muted-foreground font-medium mb-1.5">
                      E-mail <span className="text-muted-foreground/80 text-xs normal-case">(opcional / dados reais do cliente)</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-muted-foreground">
                        <Mail size={18} />
                      </div>
                      <input
                        id="expointer-field-3"
                        type="email"
                        maxLength={120}
                        value={leadData.email}
                        onChange={(e) =>
                          setLeadData((prev) => ({ ...prev, email: e.target.value }))
                        }
                        placeholder="Ex: cliente@email.com (ou deixe em branco)"
                        className="w-full pl-10 pr-4 py-3.5 rounded-xl bg-background border border-input text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary/70 focus:ring-1 focus:ring-primary/40 font-body text-base"
                      />
                    </div>
                    <span className="text-xs text-muted-foreground mt-1 block">
                      Se deixado em branco, será registrado como lead@epointer.com.br.
                    </span>
                  </div>

                  <ConsentCheckboxes
                    idPrefix="expointer"
                    contactText="Autorizo o contato por WhatsApp e e-mail sobre o sorteio e o resultado."
                    contact={lgpdAccepted}
                    onContactChange={setLgpdAccepted}
                    marketing={marketingAccepted}
                    onMarketingChange={setMarketingAccepted}
                  />

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 mt-6 rounded-full font-medium text-base transition-all shadow-gold hover:translate-y-[-1px] disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer"
                    style={{ background: "var(--gold-gradient)", color: "#0f0d0a" }}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="animate-spin" size={18} /> Validando participação...
                      </>
                    ) : (
                      <>
                        Validar Participação no Sorteio <CheckCircle2 size={18} />
                      </>
                    )}
                  </button>
                </form>

                <div className="flex items-center justify-between pt-6 mt-4 border-t border-border/40 text-xs text-muted-foreground">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors cursor-pointer"
                  >
                    <ArrowLeft size={14} /> Voltar à pergunta 2
                  </button>
                  <span className="flex items-center gap-1">
                    <ShieldCheck size={14} className="text-primary" /> Dados 100% seguros
                  </span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="border-t border-border/40 py-4 px-4 text-center text-xs text-muted-foreground">
        <div className="container max-w-4xl flex flex-col sm:flex-row items-center justify-between gap-2">
          <span>Juliano Wagner · Consultor Patrimonial ANCORD & Ademicon</span>
          <span>Expointer · Pesquisa Rápida & Captura de Leads</span>
        </div>
      </footer>
    </div>
  );
}
